import { IdcardOutlined, RocketOutlined, VideoCameraAddOutlined } from '@ant-design/icons'
import { Button, Input, Modal, Spin, message } from 'antd'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  confirmVideoKeyframeDesign,
  confirmVideoStoryboard,
  createVideoDirectorSession,
  createVideoGenTask,
  designVideoKeyframes,
  forkVideoDirectorProject,
  generateAllVideoShots,
  generateRequiredVideoKeyframes,
  generateVideoKeyframe,
  generateVideoShot,
  generateVideoStoryboard,
  getVideoDirectorSession,
  getVideoGenConfig,
  getVideoKeyframeSession,
  getVideoShotGenerationSession,
  getVideoStoryboardSession,
  patchVideoDirectorProject,
  redesignVideoKeyframes,
  regenerateVideoDirectorProposals,
  regenerateVideoKeyframe,
  regenerateVideoShot,
  regenerateVideoStoryboard,
  selectVideoDirectorProposal,
  startVideoDirectorProposals,
  updateVideoKeyframeSpec,
  videoGenAssetPosterUrl,
  type ShotGenerationSessionState,
  type VideoDirectorProposalBatchV1,
  type VideoDirectorProposalV1,
  type VideoGenConfig,
  type VideoKeyframeDesignBatchV1,
  type VideoScriptStoryboardV1,
  type VideoStoryboardBatchV1,
} from '../../api'
import { ImageAssetPicker } from '../../components/ImageAssetPicker'
import { useWorkspaceState } from '../WorkspaceContext'
import {
  RefUploadCard,
  uploadRefAsset,
  type RefAssetState,
  type RefKind,
} from '../VideoGenerationPage'
import DirectorConceptsPanel from './DirectorConceptsPanel'
import KeyframeDesignPanel from './KeyframeDesignPanel'
import { mapBatchToConcepts } from './mapDirectorProposals'
import { clearVideoDirectorDraft, saveVideoDirectorDraft } from './videoDirectorDraft'
import ShotGenerationPanel from './ShotGenerationPanel'
import StoryboardPanel from './StoryboardPanel'
import {
  canAdvanceFromStep1,
  canAdvanceToStep3,
  canAdvanceToStep4,
  canSubmitLegacyGeneration,
  computeGenerateDisabledReason,
  resolveVirtualIpRefForMode,
} from './videoDirectorLogic'
import VideoDirectorStepper from './VideoDirectorStepper'
import VideoParametersPanel from './VideoParametersPanel'
import VirtualIpContextCard from './VirtualIpContextCard'
import {
  DEFAULT_VIDEO_PARAMETERS_V1,
  USER_INTENT_MAX_LENGTH,
  type DirectorHarnessStatus,
  type VideoCreationMode,
  type VideoDirectorConceptV1,
  type VideoParametersV1,
  type VirtualIpContextRef,
} from './videoDirectorTypes'
import './video-director.css'

/**
 * VISTOERA_VIDEO_CREATION_DIRECTOR_WORKSPACE_UI_V1 — "新建视频创作".
 *
 * Reached via a button on the existing `VideoGenerationPage` list (spec
 * §二十六: progressive enhancement, the classic quick-create form on
 * that page is untouched and stays fully functional). This page owns
 * only the new 5-step Director workflow; STEP 1 is real, STEP 2-4 are
 * scaffold, and STEP 5 reuses the *real* existing `createVideoGenTask`
 * capability directly against `user_intent` (spec §二十六: never ship a
 * pretty page that cannot actually generate a video).
 */
export default function VideoDirectorCreatePage() {
  const { t } = useTranslation(['video', 'actions'])
  const { workspaceId = '' } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const sessionIdFromUrl = searchParams.get('session_id')
  const { primarySite } = useWorkspaceState()
  const siteId = primarySite?.id ?? ''

  const [cfg, setCfg] = useState<VideoGenConfig | null>(null)

  const [currentStep, setCurrentStep] = useState(1)
  const [reachedThrough, setReachedThrough] = useState(1)

  const [creationMode, setCreationMode] = useState<VideoCreationMode>('independent')
  const [virtualIpRef, setVirtualIpRef] = useState<VirtualIpContextRef | null>(null)
  const [userIntent, setUserIntent] = useState('')
  const [videoParams, setVideoParams] = useState<VideoParametersV1>(DEFAULT_VIDEO_PARAMETERS_V1)

  const [refImage, setRefImage] = useState<RefAssetState | null>(null)
  const [refVideo, setRefVideo] = useState<RefAssetState | null>(null)
  const [imagePickerOpen, setImagePickerOpen] = useState(false)
  const videoInputRef = useRef<HTMLInputElement>(null)

  const [harnessStatus, setHarnessStatus] = useState<DirectorHarnessStatus>('idle')
  const [concepts, setConcepts] = useState<VideoDirectorConceptV1[]>([])
  const [batches, setBatches] = useState<VideoDirectorProposalBatchV1[]>([])
  const [selectedConceptId, setSelectedConceptId] = useState<string | null>(null)
  const [selectionStale, setSelectionStale] = useState(false)
  const [directorSessionId, setDirectorSessionId] = useState<string | null>(null)
  const [directorError, setDirectorError] = useState<string | null>(null)
  const [selectedProposal, setSelectedProposal] = useState<VideoDirectorProposalV1 | null>(null)
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const storyboardPollRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const generatingLockRef = useRef(false)
  const storyboardLockRef = useRef(false)

  const [storyboardStatus, setStoryboardStatus] = useState<string>('idle')
  const [storyboard, setStoryboard] = useState<VideoScriptStoryboardV1 | null>(null)
  const [storyboardBatches, setStoryboardBatches] = useState<VideoStoryboardBatchV1[]>([])
  const [storyboardStale, setStoryboardStale] = useState(false)
  const [directorSelectionStale, setDirectorSelectionStale] = useState(false)
  const [confirmedStoryboardBatchId, setConfirmedStoryboardBatchId] = useState<string | null>(null)
  const [storyboardError, setStoryboardError] = useState<string | null>(null)

  const [keyframeStatus, setKeyframeStatus] = useState<string>('idle')
  // Step 3 First Render Race fix — true until the very first GET session
  // response for the *current* directorSessionId has landed. While true,
  // the panel must render an explicit loading state (never the "AI 设计关键帧"
  // empty-state action), otherwise a user with already-generated keyframes
  // sees a false "data lost" flash for ~1-3s before the real state arrives.
  const [keyframeSessionLoaded, setKeyframeSessionLoaded] = useState(false)
  const [keyframeStale, setKeyframeStale] = useState(false)
  const [keyframeStoryboardStale, setKeyframeStoryboardStale] = useState(false)
  const [keyframeAllRequiredValid, setKeyframeAllRequiredValid] = useState(false)
  const [step4Unlocked, setStep4Unlocked] = useState(false)
  const [keyframeActiveBatch, setKeyframeActiveBatch] = useState<VideoKeyframeDesignBatchV1 | null>(null)
  const [confirmedKeyframeBatchId, setConfirmedKeyframeBatchId] = useState<string | null>(null)
  const [keyframeError, setKeyframeError] = useState<string | null>(null)
  const [busyKeyframeId, setBusyKeyframeId] = useState<string | null>(null)
  const keyframePollRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // True while a "生成全部待更新关键帧" run is in flight: each poll tick
  // re-submits generate-required (cheap no-op for already-active/succeeded
  // rows) so newly-unblocked tiers of the dependency DAG (e.g. MIDDLE after
  // START succeeds) get picked up automatically, instead of requiring the
  // user to keep re-clicking the button once per DAG tier.
  const generatingAllKeyframesRef = useRef(false)
  // Mirrors keyframeActiveBatch synchronously for the poll timer closure —
  // setKeyframeActiveBatch(latest) hasn't committed yet when
  // scheduleKeyframePoll reads state inside the same call stack.
  const keyframeActiveBatchRef = useRef<VideoKeyframeDesignBatchV1 | null>(null)

  // VIDEO_GENERATION_STEP4_EXECUTION_PIPELINE_P0 — Step 4 shot-level video
  // generation execution. Status is always derived from the real Backend
  // Gate + per-shot rows (task spec §十五/§十八), never a local guess.
  const [shotGenLoaded, setShotGenLoaded] = useState(false)
  const [shotGenState, setShotGenState] = useState<ShotGenerationSessionState | null>(null)
  const [busyShotId, setBusyShotId] = useState<string | null>(null)
  const [generatingAllShots, setGeneratingAllShots] = useState(false)
  const shotGenPollRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [submittingLegacyGen, setSubmittingLegacyGen] = useState(false)
  const [legacyGenResult, setLegacyGenResult] = useState<{ taskId: string } | null>(null)
  const [projectLoading, setProjectLoading] = useState(() => Boolean(sessionIdFromUrl))
  const [projectLoadError, setProjectLoadError] = useState<string | null>(null)
  const [projectTitle, setProjectTitle] = useState('')

  // ---- Open existing project by URL session_id (never sessionStorage restore)
  const loadedSessionRef = useRef<string | null>(null)
  useEffect(() => {
    if (!workspaceId) return
    if (!sessionIdFromUrl) {
      // Bare `/video-generation` is the sidebar "视频创作" entry — show Step 1
      // immediately. A backend session is created lazily when the user starts
      // the Director workflow (`requestGenerateConcepts`), then the URL is
      // updated with `?session_id=` for refresh/resume. Never redirect to the
      // project list (that is the separate "视频项目" nav destination).
      loadedSessionRef.current = null
      setProjectLoading(false)
      setProjectLoadError(null)
      setProjectTitle('')
      setDirectorSessionId(null)
      return
    }
    if (loadedSessionRef.current === sessionIdFromUrl) return
    loadedSessionRef.current = sessionIdFromUrl
    clearVideoDirectorDraft(workspaceId)
    setProjectLoading(true)
    setProjectLoadError(null)
    setDirectorSessionId(sessionIdFromUrl)

    const stepOverride = searchParams.get('step')
    const resumeFromBackend = (projectStep: number | null | undefined) => {
      const parsed = stepOverride ? Number.parseInt(stepOverride, 10) : NaN
      const step = Number.isFinite(parsed) && parsed >= 1 && parsed <= 5
        ? parsed
        : (projectStep && projectStep >= 1 && projectStep <= 5 ? projectStep : 1)
      setCurrentStep(step)
      setReachedThrough(step)
    }

    Promise.all([
      getVideoDirectorSession(workspaceId, sessionIdFromUrl),
      getVideoStoryboardSession(workspaceId, sessionIdFromUrl),
      getVideoKeyframeSession(workspaceId, sessionIdFromUrl),
    ])
      .then(([dirResp, sbResp, kfResp]) => {
        applyDirectorState(dirResp.data)
        applyStoryboardState(sbResp.data)
        applyKeyframeState(kfResp.data)
        const project = dirResp.data.project
        setProjectTitle(project?.title || dirResp.data.user_intent || t('director.untitledProject'))
        if (dirResp.data.creation_mode) setCreationMode(dirResp.data.creation_mode as VideoCreationMode)
        if (dirResp.data.user_intent) setUserIntent(dirResp.data.user_intent)
        resumeFromBackend(project?.last_viewed_step ?? project?.current_step)
      })
      .catch((e) => {
        setProjectLoadError((e as Error).message || t('director.toast.loadProjectFailed'))
      })
      .finally(() => setProjectLoading(false))
  }, [workspaceId, sessionIdFromUrl, navigate, searchParams])

  useEffect(() => {
    if (!workspaceId || !directorSessionId) return
    saveVideoDirectorDraft(workspaceId, {
      schema_version: 'video_director_draft_v1',
      creation_mode: creationMode,
      virtual_ip_ref: creationMode === 'virtual_ip' ? virtualIpRef : null,
      user_intent: userIntent,
      reference_assets: [
        ...(refImage?.assetId
          ? [{ asset_id: refImage.assetId, role: 'reference_image' as const, media_type: 'image' as const, filename: refImage.filename }]
          : []),
        ...(refVideo?.assetId
          ? [{ asset_id: refVideo.assetId, role: 'reference_video' as const, media_type: 'video' as const, filename: refVideo.filename }]
          : []),
      ],
      video_parameters: videoParams,
      selected_director_concept_id: selectedConceptId,
      director_session_id: directorSessionId,
      updated_at: new Date().toISOString(),
    })
  }, [
    workspaceId,
    creationMode,
    virtualIpRef,
    userIntent,
    refImage,
    refVideo,
    videoParams,
    selectedConceptId,
    directorSessionId,
  ])

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current)
      if (storyboardPollRef.current) clearTimeout(storyboardPollRef.current)
      if (keyframePollRef.current) clearTimeout(keyframePollRef.current)
      if (shotGenPollRef.current) clearTimeout(shotGenPollRef.current)
    }
  }, [])

  const applyDirectorState = (data: {
    session_id: string
    status: string
    selection_stale: boolean
    selected_proposal_id: string | null
    selected_proposal?: VideoDirectorProposalV1 | null
    batches: VideoDirectorProposalBatchV1[]
    error_message: string | null
  }) => {
    setDirectorSessionId(data.session_id)
    setBatches(data.batches || [])
    setSelectionStale(!!data.selection_stale)
    setSelectedConceptId(data.selected_proposal_id)
    setSelectedProposal(data.selected_proposal ?? null)
    setDirectorError(data.error_message)
    const latest = (data.batches || []).find((b) => b.status === 'completed' && b.proposals?.length) || data.batches?.[0]
    setConcepts(mapBatchToConcepts(latest))
    if (data.status === 'queued' || data.status === 'running') {
      setHarnessStatus('generating')
      schedulePoll(data.session_id)
    } else if (data.status === 'ready') {
      setHarnessStatus('ready')
      generatingLockRef.current = false
    } else if (data.status === 'needs_regeneration') {
      setHarnessStatus('needs_regeneration')
      generatingLockRef.current = false
    } else if (data.status === 'failed') {
      setHarnessStatus('error')
      generatingLockRef.current = false
    } else {
      setHarnessStatus(latest?.proposals?.length ? 'ready' : 'idle')
      generatingLockRef.current = false
    }
  }

  const schedulePoll = (sessionId: string) => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current)
    pollTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await getVideoDirectorSession(workspaceId, sessionId)
        applyDirectorState(data)
      } catch (e) {
        setHarnessStatus('error')
        setDirectorError((e as Error).message || t('director.toast.pollDirectorFailed'))
        generatingLockRef.current = false
      }
    }, 2500)
  }

  const applyStoryboardState = (data: {
    status: string
    storyboard_stale: boolean
    director_selection_stale: boolean
    confirmed_storyboard_batch_id: string | null
    confirmed_storyboard: VideoScriptStoryboardV1 | null
    batches: VideoStoryboardBatchV1[]
    error_message: string | null
  }) => {
    setStoryboardStale(!!data.storyboard_stale)
    setDirectorSelectionStale(!!data.director_selection_stale)
    setConfirmedStoryboardBatchId(data.confirmed_storyboard_batch_id)
    setStoryboardBatches(data.batches || [])
    setStoryboardError(data.error_message)
    const latest = (data.batches || []).find((b) => b.status === 'completed' && b.storyboard) || data.batches?.[0]
    setStoryboard(latest?.storyboard ?? data.confirmed_storyboard ?? null)
    if (data.status === 'queued' || data.status === 'running') {
      setStoryboardStatus(data.status)
      scheduleStoryboardPoll()
    } else if (data.status === 'needs_regeneration') {
      setStoryboardStatus('needs_regeneration')
      storyboardLockRef.current = false
    } else if (data.status === 'failed') {
      setStoryboardStatus('failed')
      storyboardLockRef.current = false
    } else if (latest?.storyboard || data.confirmed_storyboard) {
      setStoryboardStatus('ready')
      storyboardLockRef.current = false
    } else {
      setStoryboardStatus('idle')
      storyboardLockRef.current = false
    }
  }

  const scheduleStoryboardPoll = () => {
    if (!directorSessionId || !workspaceId) return
    if (storyboardPollRef.current) clearTimeout(storyboardPollRef.current)
    storyboardPollRef.current = setTimeout(async () => {
      try {
        const { data } = await getVideoStoryboardSession(workspaceId, directorSessionId)
        applyStoryboardState(data)
      } catch (e) {
        setStoryboardStatus('failed')
        setStoryboardError((e as Error).message || t('director.toast.pollStoryboardFailed'))
        storyboardLockRef.current = false
      }
    }, 2500)
  }

  useEffect(() => {
    if (!workspaceId || !directorSessionId || currentStep !== 2) return
    getVideoStoryboardSession(workspaceId, directorSessionId)
      .then(({ data }) => applyStoryboardState(data))
      .catch(() => undefined)
  }, [workspaceId, directorSessionId, currentStep, videoParams.duration_seconds, videoParams.aspect_ratio])

  const applyKeyframeState = (data: {
    status: string
    storyboard_stale: boolean
    keyframe_design_stale: boolean
    all_required_valid: boolean
    step4_unlocked: boolean
    confirmed_keyframe_batch_id: string | null
    batches: VideoKeyframeDesignBatchV1[]
    active_batch: VideoKeyframeDesignBatchV1 | null
    error_message: string | null
  }) => {
    setKeyframeStoryboardStale(!!data.storyboard_stale)
    setKeyframeStale(!!data.keyframe_design_stale)
    setKeyframeAllRequiredValid(!!data.all_required_valid)
    setStep4Unlocked(!!data.step4_unlocked)
    setConfirmedKeyframeBatchId(data.confirmed_keyframe_batch_id)
    setKeyframeError(data.error_message)
    const latest =
      data.active_batch ??
      (data.batches || []).find((b) => b.status === 'completed' && b.keyframes?.length) ??
      data.batches?.[0] ??
      null
    setKeyframeActiveBatch(latest)
    keyframeActiveBatchRef.current = latest
    setKeyframeStatus(data.status)
    // Keep polling while the design run itself is in flight OR any keyframe
    // in the current batch still has an active generation task — the design
    // batch's own status becomes 'ready' as soon as the Skill finishes, long
    // before per-keyframe image generation (queued/running) completes, so
    // relying on `data.status` alone would leave the UI stuck showing a
    // stale "generating..." card forever once the last poll response landed.
    const keyframes = latest?.keyframes || []
    const anyKeyframeActive = keyframes.some((k) => k.status === 'queued' || k.status === 'running')
    const anyBlocked = keyframes.some((k) => k.status === 'blocked_by_dependency')
    if (generatingAllKeyframesRef.current && !anyKeyframeActive && !anyBlocked) {
      // The whole DAG has drained (every required frame is now succeeded,
      // failed, or needs_regeneration) — stop auto-cascading.
      generatingAllKeyframesRef.current = false
    }
    if (data.status === 'queued' || data.status === 'running' || anyKeyframeActive || (generatingAllKeyframesRef.current && anyBlocked)) {
      scheduleKeyframePoll()
    } else {
      setBusyKeyframeId(null)
    }
  }

  const scheduleKeyframePoll = () => {
    if (!directorSessionId || !workspaceId) return
    if (keyframePollRef.current) clearTimeout(keyframePollRef.current)
    keyframePollRef.current = setTimeout(async () => {
      try {
        const activeBatchId = keyframeActiveBatchRef.current?.design_batch_id
        const { data } =
          generatingAllKeyframesRef.current && activeBatchId
            ? await generateRequiredVideoKeyframes(workspaceId, directorSessionId, activeBatchId)
            : await getVideoKeyframeSession(workspaceId, directorSessionId)
        applyKeyframeState(data)
      } catch (e) {
        setKeyframeStatus('failed')
        setKeyframeError((e as Error).message || t('director.toast.pollKeyframesFailed'))
        setBusyKeyframeId(null)
        generatingAllKeyframesRef.current = false
      }
    }, 2500)
  }

  useEffect(() => {
    if (!workspaceId || !directorSessionId || currentStep !== 3) return
    setKeyframeSessionLoaded(false)
    getVideoKeyframeSession(workspaceId, directorSessionId)
      .then(({ data }) => applyKeyframeState(data))
      .catch(() => undefined)
      .finally(() => setKeyframeSessionLoaded(true))
  }, [workspaceId, directorSessionId, currentStep])

  const requestDesignKeyframes = async () => {
    if (!workspaceId || !directorSessionId) return
    setKeyframeStatus('queued')
    setKeyframeError(null)
    try {
      const { data } = await designVideoKeyframes(workspaceId, directorSessionId)
      applyKeyframeState(data)
    } catch (e) {
      setKeyframeStatus('failed')
      setKeyframeError((e as Error).message || t('director.toast.keyframeDesignFailed'))
    }
  }

  const requestRedesignKeyframes = async () => {
    if (!workspaceId || !directorSessionId) return
    setConfirmedKeyframeBatchId(null)
    setKeyframeStatus('queued')
    setKeyframeError(null)
    try {
      const { data } = await redesignVideoKeyframes(workspaceId, directorSessionId)
      applyKeyframeState(data)
      message.success(t('director.toast.redesignStarted'))
    } catch (e) {
      setKeyframeStatus('failed')
      setKeyframeError((e as Error).message || t('director.toast.redesignFailed'))
    }
  }

  const requestGenerateFrame = async (keyframeId: string) => {
    if (!workspaceId || !directorSessionId || !keyframeActiveBatch) return
    setBusyKeyframeId(keyframeId)
    try {
      const { data } = await generateVideoKeyframe(workspaceId, directorSessionId, keyframeActiveBatch.design_batch_id, keyframeId)
      applyKeyframeState(data)
    } catch (e) {
      message.error((e as Error).message || t('director.toast.generateKeyframeFailed'))
    } finally {
      setBusyKeyframeId(null)
    }
  }

  const requestRegenerateFrame = async (keyframeId: string) => {
    if (!workspaceId || !directorSessionId || !keyframeActiveBatch) return
    setBusyKeyframeId(keyframeId)
    try {
      const { data } = await regenerateVideoKeyframe(workspaceId, directorSessionId, keyframeActiveBatch.design_batch_id, keyframeId)
      applyKeyframeState(data)
    } catch (e) {
      message.error((e as Error).message || t('director.toast.regenerateKeyframeFailed'))
    } finally {
      setBusyKeyframeId(null)
    }
  }

  const requestGenerateRequiredFrames = async () => {
    if (!workspaceId || !directorSessionId || !keyframeActiveBatch) return
    generatingAllKeyframesRef.current = true
    try {
      const { data } = await generateRequiredVideoKeyframes(workspaceId, directorSessionId, keyframeActiveBatch.design_batch_id)
      applyKeyframeState(data)
    } catch (e) {
      generatingAllKeyframesRef.current = false
      message.error((e as Error).message || t('director.toast.generateKeyframeFailed'))
    }
  }

  const requestEditKeyframePrompt = async (keyframeId: string, prompt: string) => {
    if (!workspaceId || !directorSessionId || !keyframeActiveBatch) return
    try {
      const { data } = await updateVideoKeyframeSpec(
        workspaceId,
        directorSessionId,
        keyframeActiveBatch.design_batch_id,
        keyframeId,
        { generation_prompt_semantic: prompt },
      )
      applyKeyframeState(data)
      message.success(t('director.toast.saved'))
    } catch (e) {
      message.error((e as Error).message || t('director.toast.saveFailed'))
    }
  }

  const requestConfirmKeyframes = async () => {
    if (!workspaceId || !directorSessionId || !keyframeActiveBatch) return
    try {
      const { data } = await confirmVideoKeyframeDesign(workspaceId, directorSessionId, keyframeActiveBatch.design_batch_id)
      applyKeyframeState(data)
      message.success(t('director.toast.confirmKeyframesSuccess'))
    } catch (e) {
      message.error((e as Error).message || t('director.toast.confirmKeyframesFailed'))
    }
  }

  // ------------------------------------------------------------- Step 4

  const applyShotGenState = (data: ShotGenerationSessionState) => {
    setShotGenState(data)
    const anyActive = data.shots.some((s) => s.status === 'queued' || s.status === 'running')
    if (anyActive) {
      scheduleShotGenPoll()
    } else {
      setBusyShotId(null)
      setGeneratingAllShots(false)
    }
  }

  const scheduleShotGenPoll = () => {
    if (!directorSessionId || !workspaceId) return
    if (shotGenPollRef.current) clearTimeout(shotGenPollRef.current)
    shotGenPollRef.current = setTimeout(async () => {
      try {
        const { data } = await getVideoShotGenerationSession(workspaceId, directorSessionId)
        applyShotGenState(data)
      } catch (e) {
        message.error((e as Error).message || t('director.toast.pollVideoGenFailed'))
        setBusyShotId(null)
        setGeneratingAllShots(false)
      }
    }, 3000)
  }

  useEffect(() => {
    if (!workspaceId || !directorSessionId || currentStep !== 4) return
    setShotGenLoaded(false)
    getVideoShotGenerationSession(workspaceId, directorSessionId)
      .then(({ data }) => applyShotGenState(data))
      .catch(() => undefined)
      .finally(() => setShotGenLoaded(true))
  }, [workspaceId, directorSessionId, currentStep])

  const requestGenerateShot = async (shotId: string) => {
    if (!workspaceId || !directorSessionId) return
    setBusyShotId(shotId)
    try {
      const { data } = await generateVideoShot(workspaceId, directorSessionId, shotId)
      applyShotGenState(data)
    } catch (e) {
      message.error((e as Error).message || t('director.toast.generateVideoFailed'))
      setBusyShotId(null)
    }
  }

  const requestRegenerateShot = async (shotId: string) => {
    if (!workspaceId || !directorSessionId) return
    setBusyShotId(shotId)
    try {
      const { data } = await regenerateVideoShot(workspaceId, directorSessionId, shotId)
      applyShotGenState(data)
    } catch (e) {
      message.error((e as Error).message || t('director.toast.regenerateVideoFailed'))
      setBusyShotId(null)
    }
  }

  const requestGenerateAllShots = async () => {
    if (!workspaceId || !directorSessionId) return
    setGeneratingAllShots(true)
    try {
      const { data } = await generateAllVideoShots(workspaceId, directorSessionId)
      applyShotGenState(data)
    } catch (e) {
      message.error((e as Error).message || t('director.toast.generateAllVideosFailed'))
      setGeneratingAllShots(false)
    }
  }

  const buildDirectorInput = () => ({
    user_intent: userIntent.trim(),
    creation_mode: creationMode,
    duration_seconds: videoParams.duration_seconds,
    aspect_ratio: videoParams.aspect_ratio,
    resolution: videoParams.quality_tier,
    reference_assets: [
      ...(refImage?.assetId
        ? [
            {
              asset_id: refImage.assetId,
              media_type: 'image' as const,
              role: 'reference_image',
              filename: refImage.filename,
            },
          ]
        : []),
      ...(refVideo?.assetId
        ? [
            {
              asset_id: refVideo.assetId,
              media_type: 'video' as const,
              role: 'reference_video',
              filename: refVideo.filename,
            },
          ]
        : []),
    ],
    virtual_ip_snapshot:
      creationMode === 'virtual_ip' && virtualIpRef
        ? {
            draft_id: virtualIpRef.draft_id,
            virtual_ip_version: virtualIpRef.virtual_ip_version,
            has_intake: virtualIpRef.has_intake,
          }
        : undefined,
  })

  const requestGenerateConcepts = async () => {
    if (!workspaceId || generatingLockRef.current) return
    if (disabledGenerateReason) {
      message.warning(disabledGenerateReason)
      return
    }
    generatingLockRef.current = true
    setHarnessStatus('generating')
    setDirectorError(null)
    try {
      const input = buildDirectorInput()
      let sessionId = directorSessionId
      if (!sessionId) {
        const created = await createVideoDirectorSession(workspaceId, input)
        sessionId = created.data.session_id
        setDirectorSessionId(sessionId)
        if (!sessionIdFromUrl) {
          navigate(
            `/workspace/${workspaceId}/video-generation?session_id=${encodeURIComponent(sessionId)}`,
            { replace: true },
          )
        }
      }
      const { data } = await startVideoDirectorProposals(workspaceId, sessionId, {
        ...input,
        session_id: sessionId,
      })
      applyDirectorState(data)
    } catch (e) {
      setHarnessStatus('error')
      setDirectorError((e as Error).message || t('director.toast.proposalGenerateFailed'))
      generatingLockRef.current = false
    }
  }

  const regenerateConcepts = async () => {
    if (!workspaceId || !directorSessionId || generatingLockRef.current) return
    const hasDownstream = !!confirmedStoryboardBatchId || !!confirmedKeyframeBatchId || step4Unlocked
    let targetSessionId = directorSessionId
    if (hasDownstream) {
      const choice = await confirmUpstreamBranch(t('director.downstream.confirmedScriptKeyframesVideo'))
      if (choice === 'cancel') return
      if (choice === 'fork') {
        const newId = await forkToNewProject('IDEA')
        if (!newId) return
        targetSessionId = newId
        setDirectorSessionId(newId)
      }
    }
    generatingLockRef.current = true
    setSelectedConceptId(null)
    setHarnessStatus('generating')
    setDirectorError(null)
    try {
      const { data } = await regenerateVideoDirectorProposals(workspaceId, targetSessionId, {
        ...buildDirectorInput(),
        session_id: targetSessionId,
      })
      applyDirectorState(data)
    } catch (e) {
      setHarnessStatus('error')
      setDirectorError((e as Error).message || t('director.toast.proposalRegenerateFailed'))
      generatingLockRef.current = false
    }
  }

  const onSelectConcept = async (conceptId: string, batchId: string) => {
    if (!workspaceId || !directorSessionId || !batchId) return
    try {
      const { data } = await selectVideoDirectorProposal(workspaceId, directorSessionId, {
        batch_id: batchId,
        proposal_id: conceptId,
      })
      applyDirectorState(data)
      message.success(t('director.toast.proposalSelected'))
      getVideoStoryboardSession(workspaceId, directorSessionId)
        .then(({ data: sb }) => applyStoryboardState(sb))
        .catch(() => undefined)
    } catch (e) {
      message.error((e as Error).message || t('director.toast.proposalSelectFailed'))
    }
  }

  const requestGenerateStoryboard = async () => {
    if (!workspaceId || !directorSessionId || storyboardLockRef.current) return
    storyboardLockRef.current = true
    setStoryboardStatus('queued')
    setStoryboardError(null)
    try {
      const { data } = await generateVideoStoryboard(workspaceId, directorSessionId, buildDirectorInput())
      applyStoryboardState(data)
    } catch (e) {
      setStoryboardStatus('failed')
      setStoryboardError((e as Error).message || t('director.toast.storyboardGenerateFailed'))
      storyboardLockRef.current = false
    }
  }

  const requestRegenerateStoryboard = async () => {
    if (!workspaceId || !directorSessionId || storyboardLockRef.current) return
    storyboardLockRef.current = true
    setConfirmedStoryboardBatchId(null)
    setStoryboardStatus('queued')
    setStoryboardError(null)
    try {
      const { data } = await regenerateVideoStoryboard(workspaceId, directorSessionId, buildDirectorInput())
      applyStoryboardState(data)
    } catch (e) {
      setStoryboardStatus('failed')
      setStoryboardError((e as Error).message || t('director.toast.storyboardRegenerateFailed'))
      storyboardLockRef.current = false
    }
  }

  const onConfirmStoryboard = async () => {
    const batchId = storyboardBatches.find((b) => b.status === 'completed' && b.storyboard)?.batch_id
    if (!workspaceId || !directorSessionId || !batchId) return
    try {
      const { data } = await confirmVideoStoryboard(workspaceId, directorSessionId, { batch_id: batchId })
      applyStoryboardState(data)
      message.success(t('director.toast.storyboardConfirmed'))
    } catch (e) {
      message.error((e as Error).message || t('director.toast.storyboardConfirmFailed'))
    }
  }

  useEffect(() => {
    getVideoGenConfig(workspaceId)
      .then(({ data }) => setCfg(data))
      .catch(() => {
        /* VideoParametersPanel falls back to safe real defaults when cfg is null */
      })
  }, [workspaceId])

  const setModeAndReset = (mode: VideoCreationMode) => {
    setCreationMode(mode)
    setVirtualIpRef((prev) => resolveVirtualIpRefForMode(mode, prev))
  }

  const pickFile = (kind: RefKind) => {
    if (kind === 'reference_image') {
      if (!siteId) {
        message.error(t('director.toast.noSiteForPicker'))
        return
      }
      setImagePickerOpen(true)
      return
    }
    videoInputRef.current?.click()
  }

  const onFileSelected = async (kind: RefKind, file: File | undefined) => {
    if (!file || !workspaceId) return
    const setState = kind === 'reference_image' ? setRefImage : setRefVideo
    const previewUrl = kind === 'reference_image' ? URL.createObjectURL(file) : undefined
    setState({ file, filename: file.name, previewUrl, status: 'uploading' })
    try {
      const assetId = await uploadRefAsset(workspaceId, file, cfg?.direct_to_r2 ?? false)
      const readyPreviewUrl = kind === 'reference_video' ? videoGenAssetPosterUrl(workspaceId, assetId) : previewUrl
      setState({ file, filename: file.name, previewUrl: readyPreviewUrl, status: 'ready', assetId })
    } catch (e) {
      setState({ file, filename: file.name, previewUrl, status: 'failed', error: (e as Error).message || t('director.toast.uploadFailed') })
    }
  }

  const clearRef = (kind: RefKind) => {
    if (kind === 'reference_image') {
      if (refImage?.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(refImage.previewUrl)
      setRefImage(null)
    } else {
      setRefVideo(null)
      if (videoInputRef.current) videoInputRef.current.value = ''
    }
  }

  const disabledGenerateReasonKey = computeGenerateDisabledReason(userIntent)
  const disabledGenerateReason = disabledGenerateReasonKey ? t(`director.hints.${disabledGenerateReasonKey}`) : null

  const goStep = (step: number) => {
    if (step > reachedThrough) return
    setCurrentStep(step)
    if (workspaceId && directorSessionId) {
      void patchVideoDirectorProject(workspaceId, directorSessionId, {
        last_viewed_step: step,
        current_step: step,
      }).catch(() => undefined)
    }
  }

  const advanceTo = (step: number) => {
    setCurrentStep(step)
    setReachedThrough((r) => Math.max(r, step))
    if (workspaceId && directorSessionId) {
      void patchVideoDirectorProject(workspaceId, directorSessionId, {
        last_viewed_step: step,
        current_step: step,
      }).catch(() => undefined)
    }
  }

  const confirmUpstreamBranch = useCallback(
    (downstreamLabel: string): Promise<'update' | 'fork' | 'cancel'> =>
      new Promise((resolve) => {
        Modal.confirm({
          title: t('director.branch.title'),
          content: t('director.branch.content', { downstream: downstreamLabel }),
          okText: t('director.branch.updateProject'),
          cancelText: t('actions:cancel'),
          onOk: () => resolve('update'),
          onCancel: () => resolve('cancel'),
          footer: (_, { OkBtn, CancelBtn }) => (
            <>
              <CancelBtn />
              <Button
                onClick={() => {
                  Modal.destroyAll()
                  resolve('fork')
                }}
              >
                {t('director.branch.createProject')}
              </Button>
              <OkBtn />
            </>
          ),
        })
      }),
    [t],
  )

  const forkToNewProject = async (forkedFromStage: string) => {
    if (!workspaceId || !directorSessionId) return null
    const { data } = await forkVideoDirectorProject(workspaceId, directorSessionId, {
      forked_from_stage: forkedFromStage,
    })
    const newId = data.session_id
    navigate(`/workspace/${workspaceId}/video-generation?session_id=${encodeURIComponent(newId)}`, {
      replace: true,
    })
    return newId
  }

  const canSubmitLegacyGen = canSubmitLegacyGeneration(userIntent, refImage?.status, refVideo?.status)

  const submitLegacyGeneration = async () => {
    if (!canSubmitLegacyGen || !workspaceId) return
    setSubmittingLegacyGen(true)
    try {
      const { data } = await createVideoGenTask(workspaceId, {
        prompt: userIntent.trim(),
        aspect_ratio: videoParams.aspect_ratio,
        quality_tier: videoParams.quality_tier,
        duration_seconds: videoParams.duration_seconds,
        seed_mode: videoParams.seed_mode,
        seed: videoParams.seed_mode === 'fixed' ? videoParams.seed : null,
        reference_image_asset_id: refImage?.assetId ?? null,
        reference_video_asset_id: refVideo?.assetId ?? null,
      })
      setLegacyGenResult({ taskId: data.id })
      clearVideoDirectorDraft(workspaceId)
      message.success(t('director.toast.taskSubmitted'))
    } catch (e) {
      message.error((e as Error).message || t('director.toast.taskSubmitFailed'))
    } finally {
      setSubmittingLegacyGen(false)
    }
  }

  const paramsPanel = (
    <div className="tc-vdc-params-sticky">
      <VideoParametersPanel
        cfg={cfg}
        value={videoParams}
        onChange={setVideoParams}
        hasReferenceImage={!!refImage?.assetId}
        hasReferenceVideo={!!refVideo?.assetId}
      />
    </div>
  )

  return (
    <div className="tc-items-page tc-vdc-page">
      {projectLoading ? (
        <div className="tc-data-card" style={{ padding: 48, textAlign: 'center' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16, color: 'var(--tc-text-secondary)' }}>{t('director.loadingProject')}</div>
        </div>
      ) : projectLoadError ? (
        <div className="tc-data-card" style={{ padding: 24 }}>
          <p>{projectLoadError}</p>
          <Button onClick={() => navigate(`/workspace/${workspaceId}/video-generation/projects`)}>
            {t('director.backToProjectList')}
          </Button>
        </div>
      ) : (
        <>
      <div className="tc-page-header">
        <div>
          <h2 className="tc-page-title">{projectTitle || t('director.defaultProjectTitle')}</h2>
          <p className="tc-page-desc">{t('director.subtitle')}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={() => navigate(`/workspace/${workspaceId}/video-generation/projects`)}>
            {t('director.backToProjects')}
          </Button>
        <Button onClick={() => navigate(`/workspace/${workspaceId}/video-generation/classic`)}>
          {t('director.classicTasks')}
        </Button>
        </div>
      </div>

      <VideoDirectorStepper currentStep={currentStep} reachedThrough={reachedThrough} onStepClick={goStep} />

      {currentStep === 1 && (
        <div className="tc-vdc-step1-layout">
          <div className="tc-vdc-main-col">
            <div className="tc-data-card">
              <div className="tc-vdc-panel-title" style={{ marginBottom: 10 }}>
                {t('director.stepTitles.1')}
              </div>

              <div className="tc-video-gen-label" style={{ marginTop: 0 }}>
                {t('director.creationType')}
              </div>
              <div className="tc-vdc-mode-row">
                <button
                  type="button"
                  className={`tc-vdc-mode-option${creationMode === 'virtual_ip' ? ' is-active' : ''}`}
                  onClick={() => setModeAndReset('virtual_ip')}
                >
                  <IdcardOutlined className="tc-vdc-mode-option-icon" />
                  <div>
                    <div className="tc-vdc-mode-option-title">{t('director.modes.virtualIp.title')}</div>
                    <div className="tc-vdc-mode-option-desc">{t('director.modes.virtualIp.desc')}</div>
                  </div>
                </button>
                <button
                  type="button"
                  className={`tc-vdc-mode-option${creationMode === 'independent' ? ' is-active' : ''}`}
                  onClick={() => setModeAndReset('independent')}
                >
                  <VideoCameraAddOutlined className="tc-vdc-mode-option-icon" />
                  <div>
                    <div className="tc-vdc-mode-option-title">{t('director.modes.independent.title')}</div>
                    <div className="tc-vdc-mode-option-desc">{t('director.modes.independent.desc')}</div>
                  </div>
                </button>
              </div>

              {creationMode === 'virtual_ip' && (
                <div style={{ marginTop: 12 }}>
                  <VirtualIpContextCard onResolved={setVirtualIpRef} />
                </div>
              )}

              <div className="tc-video-gen-label">{t('director.intentLabel')}</div>
              <Input.TextArea
                value={userIntent}
                onChange={(e) => setUserIntent(e.target.value.slice(0, USER_INTENT_MAX_LENGTH))}
                placeholder={t('director.intentPlaceholder')}
                className="tc-vdc-intent-textarea"
                autoSize={{ minRows: 3, maxRows: 6 }}
              />
              <div className="tc-vdc-intent-hint">{t('director.intentHint')}</div>
              <div className="tc-video-gen-char-count">
                {userIntent.length}/{USER_INTENT_MAX_LENGTH}
              </div>

              <div className="tc-video-gen-label">{t('director.referenceMaterials')}</div>
              <div className="tc-video-gen-refs-row">
                <RefUploadCard
                  kind="reference_image"
                  label={t('director.referenceImage')}
                  hint={cfg ? cfg.image_allowed_mime.map((m) => m.split('/')[1]?.toUpperCase()).join(' / ') : 'JPG / PNG / WEBP'}
                  state={refImage}
                  onPick={() => pickFile('reference_image')}
                  onClear={() => clearRef('reference_image')}
                />
                <RefUploadCard
                  kind="reference_video"
                  label={t('director.referenceVideo')}
                  hint={cfg ? cfg.video_allowed_mime.map((m) => m.split('/')[1]?.toUpperCase()).join(' / ') : 'MP4 / MOV / WEBM'}
                  state={refVideo}
                  onPick={() => pickFile('reference_video')}
                  onClear={() => clearRef('reference_video')}
                />
              </div>
              <input
                ref={videoInputRef}
                type="file"
                accept={cfg?.video_allowed_mime.join(',') || 'video/*'}
                style={{ display: 'none' }}
                onChange={(e) => onFileSelected('reference_video', e.target.files?.[0])}
              />
            </div>

            <div className="tc-data-card">
              <DirectorConceptsPanel
                concepts={concepts}
                batches={batches}
                status={harnessStatus}
                selectedConceptId={selectedConceptId}
                selectionStale={selectionStale}
                errorMessage={directorError}
                onSelect={onSelectConcept}
                onRequestGenerate={() => void requestGenerateConcepts()}
                onRegenerate={() => void regenerateConcepts()}
                disabledReason={disabledGenerateReason}
              />
            </div>

            <div className="tc-vdc-next-bar">
              {selectionStale && <span className="tc-vdc-next-hint">{t('director.hints.upstreamStale')}</span>}
              {!selectedConceptId && !selectionStale && (
                <span className="tc-vdc-next-hint">{t('director.hints.selectProposal')}</span>
              )}
              <Button
                type="primary"
                disabled={!canAdvanceFromStep1(selectedConceptId, selectionStale)}
                onClick={() => advanceTo(2)}
                title={
                  selectionStale
                    ? t('director.hints.upstreamStaleTitle')
                    : !selectedConceptId
                      ? t('director.hints.selectProposalTitle')
                      : undefined
                }
              >
                {t('director.nextSteps.storyboard')}
              </Button>
            </div>

            <div className="tc-vdc-legacy-note">
              {t('director.legacyNote')}
              <a onClick={() => navigate(`/workspace/${workspaceId}/video-generation/classic`)}> {t('director.legacyLink')}</a>
            </div>
          </div>

          {paramsPanel}
        </div>
      )}

      {currentStep === 2 && (
        <div className="tc-vdc-step1-layout">
          <div className="tc-vdc-main-col">
            <div className="tc-data-card">
              <div className="tc-vdc-panel-title">{t('director.stepTitles.2')}</div>
              <StoryboardPanel
                storyboard={storyboard}
                selectedProposalTitle={selectedProposal?.title ?? null}
                selectedProposalSummary={selectedProposal?.one_line_concept ?? null}
                status={storyboardStatus}
                storyboardStale={storyboardStale}
                directorSelectionStale={directorSelectionStale || selectionStale}
                errorMessage={storyboardError}
                confirmedBatchId={confirmedStoryboardBatchId}
                onGenerate={() => void requestGenerateStoryboard()}
                onRegenerate={() => void requestRegenerateStoryboard()}
                onConfirm={() => void onConfirmStoryboard()}
                disabledReason={
                  !selectedConceptId || selectionStale
                    ? t('director.hints.selectValidProposal')
                    : null
                }
              />
            </div>
            <div className="tc-vdc-next-bar">
              <Button onClick={() => goStep(1)}>{t('director.prevStep')}</Button>
              {!canAdvanceToStep3(confirmedStoryboardBatchId, storyboardStale, directorSelectionStale || selectionStale) && (
                <span className="tc-vdc-next-hint">{t('director.hints.confirmStoryboard')}</span>
              )}
              <Button
                type="primary"
                disabled={!canAdvanceToStep3(confirmedStoryboardBatchId, storyboardStale, directorSelectionStale || selectionStale)}
                onClick={() => advanceTo(3)}
              >
                {t('director.nextSteps.keyframes')}
              </Button>
            </div>
          </div>
          {paramsPanel}
        </div>
      )}

      {currentStep === 3 && (
        <div className="tc-vdc-step1-layout">
          <div className="tc-vdc-main-col">
            <div className="tc-data-card">
              <div className="tc-vdc-panel-title">{t('director.stepTitles.3')}</div>
              <KeyframeDesignPanel
                workspaceId={workspaceId}
                storyboardTitle={storyboard?.title ?? null}
                loading={!keyframeSessionLoaded}
                status={keyframeStatus}
                keyframeDesignStale={keyframeStale}
                activeBatch={keyframeActiveBatch}
                allRequiredValid={keyframeAllRequiredValid}
                confirmedBatchId={confirmedKeyframeBatchId}
                errorMessage={keyframeError}
                busyKeyframeId={busyKeyframeId}
                onDesign={() => void requestDesignKeyframes()}
                onRedesign={() => void requestRedesignKeyframes()}
                onGenerateFrame={(id) => void requestGenerateFrame(id)}
                onRegenerateFrame={(id) => void requestRegenerateFrame(id)}
                onGenerateRequired={() => void requestGenerateRequiredFrames()}
                onEditPrompt={(id, prompt) => void requestEditKeyframePrompt(id, prompt)}
                onConfirm={() => void requestConfirmKeyframes()}
                disabledReason={
                  !confirmedStoryboardBatchId || keyframeStoryboardStale
                    ? t('director.hints.confirmValidStoryboard')
                    : null
                }
              />
            </div>
            <div className="tc-vdc-next-bar">
              <Button onClick={() => goStep(2)}>{t('director.prevStep')}</Button>
              {!canAdvanceToStep4(step4Unlocked) && (
                <span className="tc-vdc-next-hint">{t('director.hints.confirmKeyframes')}</span>
              )}
              <Button type="primary" disabled={!canAdvanceToStep4(step4Unlocked)} onClick={() => advanceTo(4)}>
                {t('director.nextSteps.generation')}
              </Button>
            </div>
          </div>
          {paramsPanel}
        </div>
      )}

      {currentStep === 4 && (
        <div className="tc-vdc-step1-layout">
          <div className="tc-vdc-main-col">
            <div className="tc-data-card">
              <div className="tc-vdc-panel-title" style={{ marginBottom: 10 }}>
                {t('director.stepTitles.4')}
              </div>
              <ShotGenerationPanel
                workspaceId={workspaceId}
                loading={!shotGenLoaded}
                state={shotGenState}
                busyShotId={busyShotId}
                generatingAll={generatingAllShots}
                onGenerate={(shotId) => void requestGenerateShot(shotId)}
                onRegenerate={(shotId) => void requestRegenerateShot(shotId)}
                onGenerateAll={() => void requestGenerateAllShots()}
                disabledReason={!step4Unlocked ? t('director.hints.confirmRequiredKeyframes') : null}
              />
            </div>
            <div className="tc-vdc-next-bar">
              <Button onClick={() => goStep(3)}>{t('director.prevStep')}</Button>
              <Button type="primary" onClick={() => advanceTo(5)}>
                {t('director.nextSteps.results')}
              </Button>
            </div>
          </div>
          {paramsPanel}
        </div>
      )}

      {currentStep === 5 && (
        <div className="tc-vdc-step1-layout">
          <div className="tc-vdc-main-col">
            <div className="tc-data-card tc-vdc-scaffold-card">
              <div className="tc-vdc-scaffold-title">{t('director.stepTitles.5')}</div>
              <div className="tc-vdc-scaffold-desc">
                {t('director.scaffold.desc')}
              </div>
              {legacyGenResult ? (
                <div className="tc-vdc-scaffold-list">
                  {t('director.scaffold.submitted', { taskId: legacyGenResult.taskId })}
                  <br />
                  <a onClick={() => navigate(`/workspace/${workspaceId}/video-generation/classic`)}>{t('director.scaffold.viewProgress')}</a>
                </div>
              ) : (
                <Button
                  type="primary"
                  icon={<RocketOutlined />}
                  loading={submittingLegacyGen}
                  disabled={!canSubmitLegacyGen}
                  onClick={submitLegacyGeneration}
                >
                  {t('director.scaffold.startGenerate')}
                </Button>
              )}
            </div>
            <div className="tc-vdc-next-bar">
              <Button onClick={() => goStep(4)}>{t('director.prevStep')}</Button>
            </div>
          </div>
          {paramsPanel}
        </div>
      )}

      {siteId && (
        <ImageAssetPicker
          open={imagePickerOpen}
          workspaceId={workspaceId}
          siteId={siteId}
          purpose="video_input"
          title={t('director.imagePickerTitle')}
          onCancel={() => setImagePickerOpen(false)}
          onConfirm={(selections) => {
            setImagePickerOpen(false)
            const sel = selections[0]
            if (!sel?.assetId) return
            setRefImage({
              filename: sel.filename || t('director.referenceImage'),
              previewUrl: videoGenAssetPosterUrl(workspaceId, sel.assetId),
              status: 'ready',
              assetId: sel.assetId,
            })
          }}
        />
      )}
        </>
      )}
    </div>
  )
}
